import { useEffect } from 'react';
import {
    Activity, Check
} from 'lucide-react';
import useFacilityStore from '../../store/facilityStore';
import useRegistrationStore from '../../store/useRegistrationStore';
import bloodBank from '../../assets/blood_bank.png';
import cardiac from '../../assets/cardiac.png';
import ctScan from '../../assets/ct_scan.png';
import dayCare from '../../assets/day_care.png';
import emergency from '../../assets/emergency.png';
import icu from '../../assets/icu.png';
import ip from '../../assets/ip.png';
import lab from '../../assets/lab.png';
import mammography from '../../assets/mammography.png';
import maternity from '../../assets/maternity.png';
import maternity_delivery_room from '../../assets/maternity_delivery_room.png';
import mri from '../../assets/mri.png';
import oncology from '../../assets/oncology.png';
import pet_scan from '../../assets/pet_scan.png';
import ultrasound from '../../assets/ultra_sound.png';
import x_ray from '../../assets/x-ray.png';
import other_services from '../../assets/other_services.png';
import op from '../../assets/op.png';
import home_care from '../../assets/home_care.png';
import medicine_delivery from '../../assets/medicine_delivery.png';
import tele_consultation from '../../assets/tele_consultation.png';

// Facility_ID → Image mapping
const facilityIconMap = {
    1: x_ray,
    2: emergency,
    3: lab,
    4: bloodBank,
    5: cardiac,
    6: ctScan,
    7: dayCare,
    8: ip,
    9: icu,
    10: mammography,
    11: maternity,
    12: maternity_delivery_room,
    13: mri,
    14: oncology,
    15: other_services,
    16: op,
    17: pet_scan,
    18: ultrasound,
    19: medicine_delivery,     // ← 19 = Medicine Delivery
    20: home_care,
    21: tele_consultation,
};

const FacilityServicesStep = () => {
    // Zustand stores
    const { facilities, loading: facilityLoading, fetchFacilities } = useFacilityStore();
    const { selectedServices, toggleService, formData, updateFormData, providerData } = useRegistrationStore();
    const isPharmacy = providerData?.providerType === 2;

    // Fetch facilities once
    useEffect(() => {
        fetchFacilities();
    }, [fetchFacilities]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateFormData({ [name]: value });
    };

    // Convert array to Set for checking
    const selectedSet = new Set(selectedServices);

    // Filter facilities based on provider type
    const displayedFacilities = isPharmacy
        ? facilities.filter(f => f.Facility_ID === 19)          // Only show Medicine Delivery for pharmacies
        : facilities.filter(f => f.Facility_ID !== 19);         // Show everything except Medicine Delivery for non-pharmacies

    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Activity className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        {isPharmacy ? 'Pharmacy Services' : 'Available Services'}
                    </h2>
                    <p className="text-base text-gray-600 mt-1">
                        {isPharmacy
                            ? 'Select all Services your Facility offers'
                            : 'Select all Services your Facility offers'}
                    </p>
                </div>
            </div>

            {/* Services Grid */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl">
                {facilityLoading ? (
                    <p className="text-center text-gray-500 py-8">Loading facilities...</p>
                ) : displayedFacilities.length === 0 ? (
                    <p className="text-center text-gray-600 py-8">
                        {isPharmacy
                            ? "No pharmacy-specific services available"
                            : "No services available"}
                    </p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {displayedFacilities.map((service) => {
                            const iconSource = facilityIconMap[service.Facility_ID] || other_services;
                            const isSelected = selectedSet.has(service.Facility_ID);

                            return (
                                <button
                                    key={service.Facility_ID}
                                    onClick={() => toggleService(service.Facility_ID)}
                                    className={`
                                        relative flex flex-col items-center justify-center p-4 min-h-[130px]
                                        bg-white border-2 rounded-xl transition-all duration-200 shadow-sm
                                        hover:shadow-md hover:border-primary/50
                                        ${isSelected
                                            ? 'border-primary bg-primary/5 shadow-primary/20'
                                            : 'border-gray-200'
                                        }
                                    `}
                                >
                                    <div className={`
                                        w-16 h-16 rounded-xl flex items-center justify-center mb-3 transition-all duration-200
                                        ${isSelected ? 'scale-105' : ''}
                                    `}>
                                        <img
                                            src={iconSource}
                                            alt={service.FacilityName}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>

                                    <p className={`
                                        text-xs sm:text-sm font-medium leading-tight text-center px-1
                                        ${isSelected ? 'text-primary' : 'text-gray-700'}
                                    `}>
                                        {service.FacilityName}
                                    </p>

                                    {isSelected && (
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-md">
                                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Conditional Inputs */}
                <div className="mt-8 space-y-6">
                    {(selectedSet.has(14) || selectedSet.has(15)) && (
                        <div className="mt-8">
                            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6">
                                <label className="block text-base font-semibold text-gray-800 mb-3">
                                    Facility Remarks / Additional Comments
                                </label>
                                <textarea
                                    name="facilityRemarks"
                                    value={formData.facilityRemarks || ''}
                                    onChange={handleInputChange}
                                    placeholder="Any additional info about your medical specialties or facility..."
                                    rows={4}
                                    className="w-full px-5 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm resize-y text-base"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Selection Summary */}
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-md">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Check className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-sm text-gray-600">Selected services</p>
                        <p className="text-2xl font-bold text-gray-900">{selectedServices.length}</p>
                    </div>
                </div>
                <p className="text-sm text-gray-600">
                    Out of <span className="font-semibold text-gray-800">{displayedFacilities.length}</span> available
                </p>
            </div>
        </div>
    );
};

export default FacilityServicesStep;